import { lazy, Suspense, useEffect } from 'react';
import { Switch, Route, Redirect, withRouter } from 'react-router';
import { MyProfilePage } from 'ui/profile/myProfilePage/MyProfilePage';
import { BuildingListPage } from 'ui/buildings/buildingListPage/BuildingListPage';
import { SetupArea } from 'ui/setup/setupArea/SetupArea';
import { VideoListPage, VideoCallPage } from 'ui/video';
import { BuildingPage } from 'ui/building/buildingPage';
import { ErrorPage } from 'ui/error/errorPage';
import { LoadingArea } from './loadingArea/LoadingArea';
import { Permission } from 'shared/permissions';
import { useSession } from 'hooks';
import { ElevatorPageSession } from 'ui/elevator/elevatorPageSession/ElevatorPageSession';
import { HelpPage } from 'ui/help/helpPage/HelpPage';
import { MenuItem, useMenu } from 'hooks/useMenu/useMenu';
import { adminRequiresTfa } from "util/env";

const LazyBuildingThemeEditor = lazy(async () => import('ui/building/themes/buildingThemeEditor/BuildingThemeEditor'));
const LazyAdminArea = lazy(async () => import('ui/admin/adminArea/AdminArea'));
const LazyOrgArea = lazy(async () => import('ui/org/orgArea/OrgArea'));

export const AppRouter = withRouter(() => {
    const { user, hasPermission, hasFeature } = useSession();
    const { pushMenu } = useMenu();

    const isUserTemporary = !!(user as any).temporary;
    const shouldRequireTfa = adminRequiresTfa && hasPermission(null, Permission.AllOrganizations);
    const tfaRedirectFlag = !isUserTemporary && shouldRequireTfa && !user?.tfaEnabled;

    //
    // Create the main menu when the AppRouter is mounted or the user permissions change
    //
    useEffect(() => {
        const items: MenuItem[] = [];

        // Empty menu for users who require TFA enabled
        if (tfaRedirectFlag) {
            pushMenu({ id: 'menu-top', items }, 0);
            return;
        }

        // Build menu
        if (hasPermission(null, Permission.Admin)) {
            items.push({ id: 'admin-link', link: '/admin', textKey: 'ADMIN_AREA.LINK' });
        }

        if (user?.orgId) {
            items.push({ id: 'org-link', link: '/org', textKey: 'ORG_AREA.LINK' });
        }

        if (hasPermission(null, Permission.EditBuilding, Permission.EditDevice)) {
            items.push({ id: 'setup-link', link: '/setup', textKey: 'SETUP.LINK' });
        }

        items.push({ id: 'buildings-link', link: '/buildings', textKey: 'BUILDINGS.TITLE' });

        if (hasPermission(null, Permission.ViewCalls)) {
            items.push({ id: 'video-link', link: '/video', textKey: 'VIDEO.LINK' });
        }

        pushMenu({ id: 'menu-top', items }, 0);
    }, [ pushMenu, hasPermission, tfaRedirectFlag ]);

    // Force users who require TFA enabled to profile page
    if (tfaRedirectFlag) {
        return (
            <Route component={ MyProfilePage }/>
        );
    }

    return (
        <Suspense fallback={ <LoadingArea/> }>
            <Switch>
                <Route path="/profile" component={ MyProfilePage }/>

                <Route path="/org" component={ LazyOrgArea }/>

                { hasPermission(null, Permission.EditTheme) &&
                    <Route exact path="/buildings/:urlSlug/themes/:themeId/edit" component={ LazyBuildingThemeEditor }/>
                }

                <Route path="/buildings/:urlSlug" component={ BuildingPage }/>
                <Route path="/buildings" component={ BuildingListPage }/>

                { hasPermission(null, Permission.ViewCalls) &&
                    <Route path="/elevator/:externalId" component={ ElevatorPageSession }/>
                }

                { hasPermission(null, Permission.ViewCalls) &&
                    <Route path="/video/:callId" component={ VideoCallPage }/>
                }
                { hasPermission(null, Permission.ViewCalls) &&
                    <Route exact path="/video" component={ VideoListPage }/>
                }
                { (hasPermission(null, Permission.EditBuilding, Permission.EditDevice)) &&
                    <Route path="/setup" component={ SetupArea }/>
                }

                { (hasPermission(null, Permission.Admin)) &&
                    <Route path="/admin" component={ LazyAdminArea }/>
                }

                <Route path="/help" component={ HelpPage }/>

                { hasPermission(null, Permission.AllBuildings) || !hasPermission(null, Permission.ViewCalls)
                    ? <Redirect exact from="/" to="/buildings"/>
                    : <Redirect exact from="/" to="/video"/>
                }

                { hasPermission(null, Permission.AllBuildings) || !hasPermission(null, Permission.ViewCalls)
                    ? <Redirect exact from="/login" to="/buildings"/>
                    : <Redirect exact from="/login" to="/video"/>
                }

                <Route component={ ErrorPage }/>
            </Switch>
        </Suspense>
    );
});
